import { FormHandler } from '@framework/form/form/FormHandler.tsx';
import { type RelationEditor } from '@framework/form/form/relations/useRelationEditor.ts';
import { MenuPresets } from '@framework/form/inputs/rich-text/menu/MenuPresets.tsx';
import { TextInput } from '@framework/form/inputs/TextInput.tsx';
import { FormItem } from '@framework/form/item/FormItem.tsx';
import { Drawer } from '@framework/ui/components/drawer/Drawer.tsx';
import { Button, type ButtonProps, Collapse, message } from 'antd';
import { useWatch } from 'react-hook-form';
import { v4 } from 'uuid';
import { BooleanConditionEditorInput } from '../../../../../../inputs/expressions-editor/conditions/BooleanConditionEditorInput.tsx';
import { RichTextAdvancedInput } from '../../../../../../inputs/rich-text-advanced/RichTextAdvancedInput.tsx';
import { DrawerEditorButtonRow } from '../../../../../../ui/drawer/DrawerEditorButtonRow.tsx';
import { StyledDrawerCollapse } from '../../../../../../ui/drawer/StyledDrawerCollapse.tsx';
import { RowCustomFormSwitch } from '../cells/RowCustomFormSwitch.tsx';
import {
  type ISimpleTableRow,
  type ITableRow,
  TableRowSchema,
} from '../ITableRow.ts';

const { Panel } = Collapse;

export interface ITableRowsWidgetEditDrawerProps {
  editor: RelationEditor<ITableRow>;
  buttonProps?: Omit<ButtonProps, 'onClick'>;
  index: number | null;
  setIndex: (index: number | null) => void;
}

export function TableRowsWidgetEditDrawer(
  props: ITableRowsWidgetEditDrawerProps,
) {
  const { editor, buttonProps, index, setIndex } = props;

  const columns = useWatch({ name: 'columns', defaultValue: [] });

  return (
    <>
      <Button
        {...buttonProps}
        onClick={() => {
          setIndex(null);
          const row = TableRowSchema.cast(
            {
              id: v4(),
              customRow: false,
              code: '',
              label: [{ type: 'text', text: '' }],
            },
            { assert: false },
          ) as ISimpleTableRow;
          editor.handle.open(row);
        }}
      >
        + Aggiungi Riga
      </Button>
      <Drawer
        maskClosable={false}
        title="Configurazione Riga"
        size="large"
        placement="right"
        destroyOnClose
        onClose={editor.handle.cancel}
        open={editor.handle.isVisible}
      >
        <FormHandler
          layout="vertical"
          schema={TableRowSchema as any}
          hideVisualFeedback
          initialValues={
            editor.handle.current ?? TableRowSchema.cast({}, { assert: false })
          }
          onSubmit={async rawValues => {
            try {
              const values = await TableRowSchema.validate(rawValues);
              const type = editor.handle.apply(values as ITableRow, index);
              if (type === 'updated') {
                message.success('Riga modificata');
              } else {
                message.success('Riga aggiunta');
              }
            } catch (e) {
              // biome-ignore format: <tbd>
              message.error('Si è verificato un errore nella gestione della riga');
              console.error(e);
            }
          }}
        >
          <FormItem name="code" label="Codice">
            <TextInput name="code" />
          </FormItem>

          <FormItem name="label" label="Etichetta">
            <RichTextAdvancedInput
              rows={4}
              name="label"
              menus={MenuPresets.Inline}
            />
          </FormItem>

          <RowCustomFormSwitch columns={columns} />

          {/* TODO Regole di validazione/visibilità */}
          <StyledDrawerCollapse defaultActiveKey={[]} ghost>
            <Panel header="Regole di Visualizzazione" key="view">
              <FormItem name="visible" label="Visibile">
                <BooleanConditionEditorInput name="visible" label="Visibile" />
              </FormItem>
              <FormItem name="enabled" label="Abilitato">
                <BooleanConditionEditorInput name="enabled" label="Abilitato" />
              </FormItem>
            </Panel>
          </StyledDrawerCollapse>

          <DrawerEditorButtonRow editor={editor} />
        </FormHandler>
      </Drawer>
    </>
  );
}
